// DataSourceMappingsList — shows all Object Type mappings for a data source,
// with per-mapping Materialize buttons and an "Add mapping" action that
// re-opens the MappingWizard for a different target Object Type.
//
// Design:
//   - Rendered as an expandable section inside the DataSourcesPage table row.
//   - Reuses the existing MappingWizard (step 2 of the upload wizard) standalone,
//     so the user only picks object type + column mapping; no re-upload.
//   - Reuses useCreateMaterialization for per-mapping materialize calls.

import { useState } from "react";
import { Plus, PlayCircle, Check, AlertCircle, Loader2, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useMappingsBySignature, useSoftDeleteMapping } from "@/hooks/useMappings";
import { useCreateMaterialization, useMaterializations } from "@/hooks/useMaterializations";
import { MappingWizard } from "@/components/ontology/MappingWizard";
import type { DataSource, Mapping, ObjectType, PropertyType } from "@/types/ontology";

// ── Sub-component: per-mapping row with materialize button ───────────────────

interface MappingRowProps {
  mapping: Mapping;
  dataSource: DataSource;
  onDeleted: () => void;
}

function MappingRow({ mapping, dataSource, onDeleted }: MappingRowProps) {
  const createMaterialization = useCreateMaterialization();
  const softDelete = useSoftDeleteMapping();

  // Latest materialization for this specific mapping + data source combo.
  const { materializations } = useMaterializations(dataSource.id);
  const latest = materializations.find((m) => m.mapping_id === mapping.id);

  const handleMaterialize = async () => {
    await createMaterialization.mutateAsync({
      dataSourceId: dataSource.id,
      mappingId: mapping.id,
    });
  };

  const handleDelete = async () => {
    await softDelete.mutateAsync({ id: mapping.id, objectTypeId: mapping.object_type_id });
    onDeleted();
  };

  return (
    <div className="flex items-center justify-between gap-3 px-3 py-2 rounded-md bg-muted/30 border border-border/50">
      <div className="flex items-center gap-2 min-w-0">
        <span className="text-sm font-medium truncate">{mapping.name}</span>
        <Badge variant="outline" className="text-[10px] px-1 py-0 h-4 flex-shrink-0">
          {mapping.object_type_id.slice(0, 8)}…
        </Badge>
      </div>

      <div className="flex items-center gap-2 flex-shrink-0">
        {/* Latest status badge */}
        {latest && (
          <span className="flex items-center gap-1 text-xs text-muted-foreground">
            {latest.status === "succeeded" && (
              <>
                <Check className="w-3 h-3 text-green-500" />
                {latest.rows_inserted.toLocaleString()} rows
              </>
            )}
            {latest.status === "failed" && (
              <>
                <AlertCircle className="w-3 h-3 text-destructive" />
                failed
              </>
            )}
            {(latest.status === "running" || latest.status === "pending") && (
              <>
                <Loader2 className="w-3 h-3 animate-spin" />
                running
              </>
            )}
          </span>
        )}

        <Button
          size="sm"
          variant="outline"
          className="h-7 text-xs gap-1"
          disabled={createMaterialization.isPending}
          onClick={handleMaterialize}
          data-testid={`materialize-${mapping.id}`}
        >
          {createMaterialization.isPending ? (
            <Loader2 className="w-3 h-3 animate-spin" />
          ) : (
            <PlayCircle className="w-3 h-3" />
          )}
          Materialize
        </Button>

        <Button
          size="icon"
          variant="ghost"
          className="h-7 w-7 text-muted-foreground hover:text-destructive"
          onClick={handleDelete}
          disabled={softDelete.isPending}
          aria-label="Delete mapping"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </Button>
      </div>
    </div>
  );
}

// ── Add-mapping dialog — reuses MappingWizard standalone ────────────────────

interface AddMappingDialogProps {
  open: boolean;
  onClose: () => void;
  dataSource: DataSource;
  existingSignatures: Set<string>; // object_type_ids already mapped, to filter dropdown
}

function AddMappingDialog({ open, onClose, dataSource }: AddMappingDialogProps) {
  const [selectedObjectType, setSelectedObjectType] = useState<ObjectType | null>(null);
  const [propertyTypes, setPropertyTypes] = useState<PropertyType[]>([]);

  const handleObjectTypeSelected = (ot: ObjectType, props: PropertyType[]) => {
    setSelectedObjectType(ot);
    setPropertyTypes(props);
  };

  const handleMappingSaved = (_mapping: Mapping) => {
    onClose();
  };

  const handleClose = () => {
    setSelectedObjectType(null);
    setPropertyTypes([]);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Add Object Type Mapping — {dataSource.name}</DialogTitle>
        </DialogHeader>

        <MappingWizard
          headers={dataSource.schema.columns.map((c) => c.name)}
          schema={dataSource.schema}
          dataSource={dataSource}
          autoAppliedMapping={null}
          selectedObjectType={selectedObjectType}
          propertyTypes={propertyTypes}
          onObjectTypeSelected={handleObjectTypeSelected}
          onMappingSaved={handleMappingSaved}
        />
      </DialogContent>
    </Dialog>
  );
}

// ── Main export ───────────────────────────────────────────────────────────────

export interface DataSourceMappingsListProps {
  dataSource: DataSource;
}

export function DataSourceMappingsList({ dataSource }: DataSourceMappingsListProps) {
  const [addOpen, setAddOpen] = useState(false);
  const { mappings, isLoading } = useMappingsBySignature(dataSource.schema.header_signature);

  // Build a set of object_type_ids already mapped (passed to dialog for awareness).
  const mappedTypeIds = new Set(mappings.map((m) => m.object_type_id));

  if (isLoading) {
    return (
      <div className="space-y-1 px-1">
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-8 w-3/4" />
      </div>
    );
  }

  return (
    <div className="space-y-2 pt-1" data-testid="datasource-mappings-list">
      {mappings.length === 0 ? (
        <p className="text-xs text-muted-foreground px-1">
          No Object Type mappings yet for this data source.
        </p>
      ) : (
        <div className="space-y-1">
          {mappings.map((m) => (
            <MappingRow key={m.id} mapping={m} dataSource={dataSource} onDeleted={() => {}} />
          ))}
        </div>
      )}

      <Button
        size="sm"
        variant="outline"
        className="h-7 text-xs gap-1 mt-1"
        onClick={() => setAddOpen(true)}
        data-testid="add-mapping-btn"
      >
        <Plus className="w-3 h-3" />
        Add Object Type mapping
      </Button>

      <AddMappingDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        dataSource={dataSource}
        existingSignatures={mappedTypeIds}
      />
    </div>
  );
}
